import React, { useState } from 'react';
import styled from 'styled-components';
import { Mail, Phone, MapPin, Send, MessageCircle, HeadphonesIcon, Building, Clock, Globe, Shield, Award, CheckCircle } from 'lucide-react';
import LogoPng from '../../assets/InitX_Logo.png';
import SEO from '../../components/SEO';

const ContactContainer = styled.div`
  background: linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%);
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif;
`;

const Header = styled.div`
  background: rgba(135,206,235,0.1);
  backdrop-filter: blur(15px);
  padding: 40px 0;
  border-bottom: 1px solid rgba(135,206,235,0.3);
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
`;

const HeaderContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    padding: 0 16px;
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }
`;

const BrandSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
    justify-content: center;
    width: 100%;
    text-align: center;
  }
`;

const StatusBadge = styled.div`
  background: rgba(135,206,235,0.2);
  backdrop-filter: blur(10px);
  color: #87ceeb;
  padding: 8px 16px;
  border-radius: 24px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid rgba(135,206,235,0.3);
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  
  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 11px;
    gap: 6px;
  }
`;

const MainContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 40px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const FormSection = styled.div`
  background: rgba(135,206,235,0.1);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(135,206,235,0.3);
  border-radius: 25px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.12);
  overflow: hidden;
`;

const FormHeader = styled.div`
  padding: 32px;
  border-bottom: 1px solid rgba(135,206,235,0.2);
  background: rgba(135,206,235,0.05);
`;

const FormBody = styled.form`
  padding: 32px;
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InfoCard = styled.div`
  background: rgba(135,206,235,0.1);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(135,206,235,0.3);
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-5px);
    border-color: rgba(135,206,235,0.5);
    box-shadow: 0 8px 30px rgba(0,0,0,0.15);
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid rgba(135,206,235,0.3);
  background: rgba(255,255,255,0.1);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  font-size: 16px;
  color: #ffffff;
  transition: all 0.2s ease;
  
  &::placeholder {
    color: rgba(255,255,255,0.6);
  }
  
  &:focus {
    outline: none;
    border-color: #87ceeb;
    background: rgba(255,255,255,0.15);
    box-shadow: 0 0 0 3px rgba(135,206,235,0.2);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid rgba(135,206,235,0.3);
  background: rgba(255,255,255,0.1);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  font-size: 16px;
  color: #ffffff;
  min-height: 120px;
  resize: vertical;
  transition: all 0.2s ease;
  font-family: inherit;
  
  &::placeholder {
    color: rgba(255,255,255,0.6);
  }
  
  &:focus {
    outline: none;
    border-color: #87ceeb;
    background: rgba(255,255,255,0.15);
    box-shadow: 0 0 0 3px rgba(135,206,235,0.2);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid rgba(135,206,235,0.3);
  background: rgba(255,255,255,0.1);
  backdrop-filter: blur(10px);
  border-radius: 12px;
  font-size: 16px;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  
  option {
    background: #1e3a8a;
    color: #ffffff;
  }
  
  &:focus {
    outline: none;
    border-color: #87ceeb;
    background: rgba(255,255,255,0.15);
    box-shadow: 0 0 0 3px rgba(135,206,235,0.2);
  }
`;

const SubmitButton = styled.button`
  background: linear-gradient(135deg, #87ceeb 0%, #1e3a8a 100%);
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 25px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(135,206,235,0.3);
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #5f9ea0 0%, #1e40af 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(135,206,235,0.4);
  }
  
  &:disabled {
    background: rgba(135,206,235,0.3);
    cursor: not-allowed;
    transform: none;
  }
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #87ceeb 0%, #1e3a8a 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    city: '',
    phone: '',
    subject: '',
    inquiryType: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const message = `Hi InitX Technologies!

New Contact Form Submission:

*Name:* ${formData.name}
*Email:* ${formData.email}
*City:* ${formData.city}
*Phone:* ${formData.phone}
*Subject:* ${formData.subject}
*Inquiry Type:* ${formData.inquiryType}
*Message:* ${formData.message}

Please get back to me soon. Thank you!`;

    const whatsappURL = `https://wa.me/918986283243?text=${encodeURIComponent(message)}`;

    setTimeout(() => {
      setFormData({ name: '', email: '', city: '', phone: '', subject: '', inquiryType: '', message: '' });
      setIsSubmitting(false);
      window.open(whatsappURL, '_blank');
    }, 1000);
  };

  const contactInfo = [
    {
      icon: <Building size={20} />,
      title: "Corporate Office",
      details: "InitX Technologies",
      subtitle: "Lucknow, Uttar Pradesh - 226013"
    },
    {
      icon: <Mail size={20} />,
      title: "Business Inquiries",
      details: "contact@initx.online",
      subtitle: "For partnerships and enterprise solutions"
    },
    {
      icon: <Phone size={20} />,
      title: "24/7 Support Hotline",
      details: "+91-8986283243",
      subtitle: "Mon-Sun 24/7"
    },
    {
      icon: <Clock size={20} />,
      title: "Response Time",
      details: "< 2 hours",
      subtitle: "Average response time for business inquiries"
    },
    {
      icon: <Shield size={20} />,
      title: "Security & Compliance",
      details: "ISO 27001 | SOC 2 Type II",
      subtitle: "Enterprise-grade security standards"
    }
  ];

  const inquiryTypes = [
    'Web Development',
    'Mobile App Development',
    'Cloud Solutions',
    'AI & Machine Learning',
    'Digital Transformation',
    'Technical Consulting',
    'Other'
  ];

  return (
    <ContactContainer>
      <SEO
        title="Contact InitX Technologies | Get a Free Consultation"
        description="Contact InitX Technologies for AI development, cloud solutions, web and mobile apps, and digital transformation consulting."
        keywords="contact InitX Technologies, consultation, software development, IT consulting, AI services, cloud solutions"
        type="website"
        siteName="InitX Technologies"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Contact', url: '/contact' }
        ]}
      />
      <Header>
        <HeaderContent>
          <BrandSection>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }} className="brand-top-layer">
              <img src={LogoPng} alt="InitX Technologies" className="brand-logo" style={{ height: '48px', width: 'auto' }} />
              <h1 className="brand-title" style={{ fontSize: '24px', fontWeight: '600', margin: '0', color: '#ffffff', letterSpacing: '-0.025em', fontFamily: 'SF Pro Display, -apple-system, system-ui, sans-serif' }}>InitX</h1>
            </div>
          </BrandSection>
          <StatusBadge>
            <div style={{ width: '8px', height: '8px', background: '#00ff00', borderRadius: '50%', boxShadow: '0 0 8px rgba(0,255,0, 0.6)' }}></div>
            <span style={{ color: '#00ff00', fontWeight: '700', textShadow: '0 0 10px rgba(0,255,0,0.3)' }}>Available</span>
            <span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '12px' }}>• Response within 2 hours</span>
          </StatusBadge>
        </HeaderContent>
      </Header>

      <MainContent>
        <FormSection>
          <FormHeader>
            <h2 style={{ fontSize: '28px', fontWeight: '700', margin: '0 0 8px 0', color: '#ffffff' }}>Get in Touch</h2>
            <p style={{ fontSize: '16px', margin: '0', color: 'rgba(255,255,255,0.8)', lineHeight: '1.5' }}>Have a project in mind? Let's discuss how we can help you achieve your goals.</p>
          </FormHeader>
          
          <FormBody onSubmit={handleSubmit}>
            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <FormGroup style={{ marginBottom: 0 }}>
                <Label>Full Name *</Label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  required
                />
              </FormGroup>
              
              <FormGroup style={{ marginBottom: 0 }}>
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="your@email.com"
                  required
                />
              </FormGroup>
            </div>
            
            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <FormGroup style={{ marginBottom: 0 }}>
                <Label>City</Label>
                <Input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Your city"
                />
              </FormGroup>
              
              <FormGroup style={{ marginBottom: 0 }}>
                <Label>Phone Number</Label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1 (555) 123-4567"
                />
              </FormGroup>
            </div>

            <div className="form-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
              <FormGroup style={{ marginBottom: 0 }}>
                <Label>Subject *</Label>
                <Input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Brief description"
                  required
                />
              </FormGroup>
              
              <FormGroup style={{ marginBottom: 0 }}>
                <Label>Inquiry Type</Label>
                <Select
                  name="inquiryType"
                  value={formData.inquiryType}
                  onChange={handleChange}
                >
                  <option value="">Select service</option>
                  {inquiryTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </Select>
              </FormGroup>
            </div>

            <FormGroup>
              <Label>Message *</Label>
              <TextArea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your project requirements..."
                required
              />
            </FormGroup>

            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <div style={{ width: '16px', height: '16px', border: '2px solid #ffffff', borderTop: '2px solid transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Send Message
                </>
              )}
            </SubmitButton>
          </FormBody>
        </FormSection>

        <ContactInfo>
          {contactInfo.map((info, index) => (
            <InfoCard key={index}>
              <InfoItem>
                <IconWrapper>{info.icon}</IconWrapper>
                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', margin: '0 0 4px 0', color: '#ffffff' }}>{info.title}</h3>
                  <p style={{ fontSize: '14px', margin: '0 0 2px 0', color: '#87ceeb', fontWeight: '500' }}>{info.details}</p>
                  <p style={{ fontSize: '13px', margin: '0', color: 'rgba(255,255,255,0.7)' }}>{info.subtitle}</p>
                </div>
              </InfoItem>
            </InfoCard>
          ))}
        </ContactInfo>
      </MainContent>
      
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @media (max-width: 768px) {
          .brand-logo {
            height: 40px !important;
          }
          .brand-title {
            font-size: 32px !important;
            font-weight: 700 !important;
          }
          .brand-subtitle {
            font-size: 12px !important;
          }
          .form-row {
            grid-template-columns: 1fr !important;
          }
          .brand-top-layer {
            justify-content: center !important;
            width: 100% !important;
          }
        }
      `}</style>
    </ContactContainer>
  );
};

export default Contact;
